import { RefObject, useLayoutEffect, useRef } from 'react';

type Layer = { panel: HTMLElement; close: () => void };
const layers: Layer[] = [];
const originals = new Map<Element, { inert: string | null; hidden: string | null }>();
let originalOverflow = '';
const focusable = (panel: HTMLElement) => Array.from(panel.querySelectorAll<HTMLElement>(
    'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), summary, [tabindex="0"]'
)).filter(element => element.getClientRects().length && !element.closest('[inert]'));
const focusFirst = (panel: HTMLElement) => (focusable(panel)[0] || panel).focus({ preventScroll: true });

function syncBackground() {
    const top = layers[layers.length - 1];
    if (!top) {
        originals.forEach((value, element) => {
            if (value.inert === null) element.removeAttribute('inert'); else element.setAttribute('inert', value.inert);
            if (value.hidden === null) element.removeAttribute('aria-hidden'); else element.setAttribute('aria-hidden', value.hidden);
        });
        originals.clear();
        document.body.style.overflow = originalOverflow;
        return;
    }
    for (const element of Array.from(document.body.children)) {
        if (!originals.has(element)) originals.set(element, { inert: element.getAttribute('inert'), hidden: element.getAttribute('aria-hidden') });
        if (element.contains(top.panel)) {
            element.removeAttribute('inert'); element.removeAttribute('aria-hidden');
        } else {
            element.setAttribute('inert', ''); element.setAttribute('aria-hidden', 'true');
        }
    }
    document.body.style.overflow = 'hidden';
}

function handleKey(event: KeyboardEvent) {
    const top = layers[layers.length - 1];
    if (!top) return;
    if (event.key === 'Escape') {
        event.preventDefault(); event.stopImmediatePropagation(); top.close();
    } else if (event.key === 'Tab') {
        const items = focusable(top.panel), first = items[0], last = items[items.length - 1];
        if (!first) { event.preventDefault(); top.panel.focus(); }
        else if (event.shiftKey && (document.activeElement === first || !top.panel.contains(document.activeElement))) {
            event.preventDefault(); last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !top.panel.contains(document.activeElement))) {
            event.preventDefault(); first.focus();
        }
    }
}

// One owner for stacked dialogs, including removal of a parent and its children together.
export function useDialogLayer(ref: RefObject<HTMLDivElement | null>, open: boolean, onClose: () => void, restoreFocusSelector?: string) {
    const close = useRef(onClose);
    close.current = onClose;
    useLayoutEffect(() => {
        const panel = ref.current;
        if (!open || !panel) return;
        const previous = document.activeElement as HTMLElement | null;
        let overlay: HTMLElement = panel;
        while (overlay.parentElement && overlay.parentElement !== document.body) overlay = overlay.parentElement;
        const originalZIndex = overlay.style.zIndex;
        if (!layers.length) {
            originalOverflow = document.body.style.overflow;
            document.addEventListener('keydown', handleKey, true);
        }
        const layer = { panel, close: () => close.current() };
        layers.push(layer);
        overlay.style.zIndex = String(1000 + layers.length * 10);
        syncBackground();
        focusFirst(panel);
        return () => {
            layers.splice(layers.indexOf(layer), 1);
            overlay.style.zIndex = originalZIndex;
            syncBackground();
            if (!layers.length) document.removeEventListener('keydown', handleKey, true);
            queueMicrotask(() => {
                const top = layers[layers.length - 1];
                const target = previous?.isConnected ? previous : restoreFocusSelector ? document.querySelector<HTMLElement>(restoreFocusSelector) : null;
                if (target?.isConnected && !target.closest('[inert]') && (!top || top.panel.contains(target))) {
                    if (!target.matches('a[href], button, input, select, textarea, [tabindex]')) target.tabIndex = -1;
                    target.focus({ preventScroll: true });
                }
                else if (top && !top.panel.contains(document.activeElement)) focusFirst(top.panel);
            });
        };
    }, [ref, open, restoreFocusSelector]);
}
